/**
 * Scope / filterScope id plane helpers (SPA crosswalk X2 / X3).
 * Structure and queue scopes are CA instance ids (``ca-*`` or Postgres UUID).
 * Compare with UUID casefold; do not invent MC tile keywords ↔ CA without structure.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidScopeId(id: string | null | undefined): boolean {
  return Boolean(id && UUID_RE.test(id.trim()));
}

/** Normalize for equality: trim; UUIDs compared case-insensitively. */
export function normalizeScopeIdKey(id: string | null | undefined): string {
  const raw = (id ?? '').trim();
  if (!raw) return '';
  return isUuidScopeId(raw) ? raw.toLowerCase() : raw;
}

export function scopeIdsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = normalizeScopeIdKey(a);
  const right = normalizeScopeIdKey(b);
  if (!left || !right) return false;
  return left === right;
}

/** True when ``needle`` matches any entry in ``candidates`` (UUID-normalized). */
export function scopeIdMatchesAny(
  needle: string | null | undefined,
  candidates: ReadonlyArray<string | null | undefined>
): boolean {
  const want = normalizeScopeIdKey(needle);
  if (!want) return false;
  return candidates.some((c) => normalizeScopeIdKey(c) === want);
}

/**
 * Best scope key on a queue / health row for matching a structure ``scopeId``.
 * Prefers ``filterScope``, then ``domain``, then optional ``itemId`` / ``waypointRef``.
 */
export function queueItemScopeKeys(item: {
  filterScope?: string;
  domain?: string;
  itemId?: string;
  waypointRef?: string;
}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [item.filterScope, item.domain, item.waypointRef, item.itemId]) {
    const key = normalizeScopeIdKey(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export function queueItemMatchesScopeId(
  item: {
    filterScope?: string;
    domain?: string;
    itemId?: string;
    waypointRef?: string;
  },
  scopeId: string | null | undefined
): boolean {
  const want = normalizeScopeIdKey(scopeId);
  if (!want) return false;
  return queueItemScopeKeys(item).includes(want);
}

/** Strip ``-annual-fy26`` / ``-YYYY-MM`` so Diagnose can join structure lane ids. */
const PERIOD_SCOPE_ID_RE = /(?:-annual-fy\d+(?:-v\d+)?|-\d{4}-\d{2})$/i;

export function collapsePeriodScopeId(id: string | null | undefined): string {
  const raw = normalizeScopeIdKey(id);
  if (!raw) return '';
  return raw.replace(PERIOD_SCOPE_ID_RE, '');
}

/**
 * Diagnose / Drive occupancy join: exact scope, period collapse, or ``lane-`` prefix
 * (FY26 annual item ids onto the base domain / subdomain tile).
 */
export function queueItemMatchesStructureScopeId(
  item: {
    filterScope?: string;
    domain?: string;
    itemId?: string;
    waypointRef?: string;
  },
  scopeId: string | null | undefined
): boolean {
  const want = normalizeScopeIdKey(scopeId);
  if (!want) return false;
  const wantCollapsed = collapsePeriodScopeId(want);
  for (const key of queueItemScopeKeys(item)) {
    if (key === want) return true;
    if (key.startsWith(`${want}-`)) return true;
    const keyCollapsed = collapsePeriodScopeId(key);
    if (keyCollapsed === want) return true;
    if (wantCollapsed === key) return true;
    if (wantCollapsed && keyCollapsed && wantCollapsed === keyCollapsed) return true;
  }
  return false;
}
